import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "../../api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Save, Trash2, Plus, Upload, ExternalLink, FileText, Image as ImageIcon, Video, Wrench, Sofa, ListChecks, CalendarClock, MapPinned, Rocket, Lock, Building2 } from "lucide-react";

const money = (v) => "€ " + (Number(v || 0)).toLocaleString("it-IT");

function Section({ icon: Icon, title, action, children, testid }) {
  return (
    <Card className="border-slate-200 p-5" data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Icon size={16} /> {title}</div>
        {action}
      </div>
      {children}
    </Card>
  );
}

export default function PipelineDetail({ id, meta, onBack }) {
  const [doc, setDoc] = useState(null);
  const [owners, setOwners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showConvert, setShowConvert] = useState(false);
  const [conv, setConv] = useState({ owner_action: "skip", owner_id: "", new_owner_name: "" });
  const docFileRef = useRef();
  const photoFileRef = useRef();

  const load = useCallback(() => {
    api.get(`/pipeline/${id}`).then((r) => setDoc(r.data)).catch(() => toast.error("Errore caricamento"));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/owners").then((r) => setOwners(r.data)).catch(() => {}); }, []);

  if (!doc) return <p className="py-16 text-center text-sm text-slate-400">Caricamento…</p>;
  const readonly = doc.stato === "convertito";
  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));
  const statoMeta = (key) => (meta?.stati || []).find((s) => s.key === key) || { label: key, color: "#64748b" };

  const saveMain = async () => {
    try {
      setSaving(true);
      const fields = ["name", "address", "zone", "city", "max_guests", "bedrooms", "bathrooms", "description",
        "ref_name", "ref_surname", "ref_company", "ref_email", "ref_phone", "ref_whatsapp", "ref_notes",
        "owner_id", "canone_richiesto", "commission_pct", "economic_model", "commercial_notes", "internal_notes", "stato"];
      const payload = {}; fields.forEach((f) => { payload[f] = doc[f]; });
      const r = await api.put(`/pipeline/${id}`, payload);
      setDoc(r.data);
      toast.success("Salvato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setSaving(false); }
  };

  const changeStato = async (v) => {
    set("stato", v);
    try { await api.put(`/pipeline/${id}`, { stato: v }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const addItem = async (listname, item) => {
    try { await api.post(`/pipeline/${id}/list/${listname}`, item); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const updItem = async (listname, itemId, patch) => {
    try { await api.put(`/pipeline/${id}/list/${listname}/${itemId}`, patch); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const delItem = async (listname, itemId) => {
    try { await api.delete(`/pipeline/${id}/list/${listname}/${itemId}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const uploadFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post(`/pipeline/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const onUploadDoc = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const up = await uploadFile(f); await addItem("documents", { category: "Altro", title: f.name, name: f.name, url: up.url, version: "1", status: "active" }); toast.success("Documento caricato"); }
    catch (er) { toast.error(er?.response?.data?.detail || "Errore upload"); }
    e.target.value = "";
  };
  const onUploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const up = await uploadFile(f); await addItem("photos", { url: up.url, name: f.name }); toast.success("Foto caricata"); }
    catch (er) { toast.error(er?.response?.data?.detail || "Errore upload"); }
    e.target.value = "";
  };

  const doConvert = async () => {
    try {
      const r = await api.post(`/pipeline/${id}/convert`, conv);
      toast.success(`Convertito in ${r.data.apartment.code} · ${r.data.apartment.name}`);
      setShowConvert(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore conversione"); }
  };

  const canConvert = !readonly && ["contratto_firmato", "in_preparazione", "pronto_attivazione"].includes(doc.stato);
  const sm = statoMeta(doc.stato);

  return (
    <div className="space-y-5 pb-24" data-testid="pipeline-detail">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} data-testid="pipeline-back"><ArrowLeft size={18} /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-slate-900">{doc.name || doc.address || "Opportunità"}</h2>
              <span className="rounded-full px-2.5 py-0.5 text-[11px] font-medium text-white" style={{ backgroundColor: sm.color }}>{sm.label}</span>
            </div>
            {doc.converted_apartment && (
              <div className="mt-0.5 text-xs font-medium text-emerald-600">🏠 Convertito nell'appartamento "{doc.converted_apartment.code} · {doc.converted_apartment.name}"</div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!readonly && (
            <Select value={doc.stato} onValueChange={changeStato}>
              <SelectTrigger className="h-9 w-52" data-testid="pipeline-stato-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.stati || []).filter((s) => s.key !== "convertito").map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {canConvert && <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowConvert(true)} data-testid="pipeline-convert-btn"><Rocket size={15} /> Converti in Appartamento</Button>}
          {!readonly && <Button className="gap-2" onClick={saveMain} disabled={saving} data-testid="pipeline-save"><Save size={15} /> Salva</Button>}
        </div>
      </div>

      {readonly && (
        <Card className="border-emerald-200 bg-emerald-50 p-4" data-testid="pipeline-readonly-banner">
          <div className="flex items-center gap-2 text-sm text-emerald-800"><Lock size={15} /> Trattativa <b>convertita</b> — scheda in sola lettura (storico commerciale conservato).</div>
        </Card>
      )}

      {/* Dati generali + economics */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Section icon={Building2} title="Dati immobile" testid="pipeline-general">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input disabled={readonly} placeholder="Nome immobile" value={doc.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="pd-name" />
            <Input disabled={readonly} placeholder="Zona" value={doc.zone || ""} onChange={(e) => set("zone", e.target.value)} />
            <Input disabled={readonly} placeholder="Indirizzo" value={doc.address || ""} onChange={(e) => set("address", e.target.value)} className="sm:col-span-2" />
            <Input disabled={readonly} type="number" placeholder="Ospiti" value={doc.max_guests || 0} onChange={(e) => set("max_guests", parseInt(e.target.value) || 0)} />
            <div className="grid grid-cols-2 gap-2">
              <Input disabled={readonly} type="number" placeholder="Camere" value={doc.bedrooms || 0} onChange={(e) => set("bedrooms", parseInt(e.target.value) || 0)} />
              <Input disabled={readonly} type="number" placeholder="Bagni" value={doc.bathrooms || 0} onChange={(e) => set("bathrooms", parseInt(e.target.value) || 0)} />
            </div>
            <Textarea disabled={readonly} placeholder="Descrizione" value={doc.description || ""} onChange={(e) => set("description", e.target.value)} className="sm:col-span-2" />
          </div>
        </Section>

        <Section icon={FileText} title="Trattativa & modello economico" testid="pipeline-econ">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className="text-xs text-slate-500">Canone richiesto (€/mese)</label><Input disabled={readonly} type="number" value={doc.canone_richiesto || 0} onChange={(e) => set("canone_richiesto", parseFloat(e.target.value) || 0)} data-testid="pd-canone" /></div>
            <div><label className="text-xs text-slate-500">Commissione %</label><Input disabled={readonly} type="number" value={doc.commission_pct || 0} onChange={(e) => set("commission_pct", parseFloat(e.target.value) || 0)} /></div>
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-500">Modello economico</label>
              <Select disabled={readonly} value={doc.economic_model || "da_definire"} onValueChange={(v) => set("economic_model", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.economic_models || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Textarea disabled={readonly} placeholder="Note commerciali" value={doc.commercial_notes || ""} onChange={(e) => set("commercial_notes", e.target.value)} className="sm:col-span-2" />
            <Textarea disabled={readonly} placeholder="Note interne" value={doc.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} className="sm:col-span-2" />
          </div>
        </Section>
      </div>

      {/* Referente + proprietario */}
      <Section icon={Building2} title="Referente & proprietario" testid="pipeline-referente">
        <div className="grid gap-3 sm:grid-cols-3">
          <Input disabled={readonly} placeholder="Nome referente *" value={doc.ref_name || ""} onChange={(e) => set("ref_name", e.target.value)} data-testid="pd-refname" />
          <Input disabled={readonly} placeholder="Cognome" value={doc.ref_surname || ""} onChange={(e) => set("ref_surname", e.target.value)} />
          <Input disabled={readonly} placeholder="Azienda" value={doc.ref_company || ""} onChange={(e) => set("ref_company", e.target.value)} />
          <Input disabled={readonly} placeholder="Email" value={doc.ref_email || ""} onChange={(e) => set("ref_email", e.target.value)} />
          <Input disabled={readonly} placeholder="Telefono" value={doc.ref_phone || ""} onChange={(e) => set("ref_phone", e.target.value)} />
          <Input disabled={readonly} placeholder="WhatsApp" value={doc.ref_whatsapp || ""} onChange={(e) => set("ref_whatsapp", e.target.value)} />
          <div className="sm:col-span-3">
            <label className="text-xs text-slate-500">Collega a Proprietario esistente (opzionale)</label>
            <Select disabled={readonly} value={doc.owner_id || "none"} onValueChange={(v) => set("owner_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="pd-owner-link"><SelectValue placeholder="Nessun collegamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessun collegamento</SelectItem>
                {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      {/* Documents */}
      <Section icon={FileText} title="Documenti (APE, planimetria, visura, contratto…)" testid="pipeline-documents"
        action={!readonly && <><input ref={docFileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={onUploadDoc} data-testid="pd-doc-input" /><Button size="sm" variant="outline" className="gap-1" onClick={() => docFileRef.current?.click()} data-testid="pd-doc-upload"><Upload size={14} /> Carica</Button></>}>
        {(doc.documents || []).length === 0 ? <p className="text-xs text-slate-400">Nessun documento.</p> : (
          <div className="space-y-2">
            {doc.documents.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 p-2" data-testid={`pd-doc-${d.id}`}>
                <Select disabled={readonly} value={d.category || "Altro"} onValueChange={(v) => { updItem("documents", d.id, { category: v }); set("documents", doc.documents.map((x) => x.id === d.id ? { ...x, category: v } : x)); }}>
                  <SelectTrigger className="h-8 w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.doc_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input disabled={readonly} className="h-8 min-w-[160px] flex-1" value={d.title || ""} placeholder="Titolo" onChange={(e) => set("documents", doc.documents.map((x) => x.id === d.id ? { ...x, title: e.target.value } : x))} onBlur={(e) => updItem("documents", d.id, { title: e.target.value })} />
                <span className="text-xs text-slate-400">v{d.version || "1"}</span>
                {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-indigo-600"><ExternalLink size={15} /></a>}
                {!readonly && <Button size="icon" variant="ghost" className="text-red-500" onClick={() => delItem("documents", d.id)}><Trash2 size={14} /></Button>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Photos + Videos */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Section icon={ImageIcon} title="Foto" testid="pipeline-photos"
          action={!readonly && <><input ref={photoFileRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={onUploadPhoto} data-testid="pd-photo-input" /><Button size="sm" variant="outline" className="gap-1" onClick={() => photoFileRef.current?.click()} data-testid="pd-photo-upload"><Upload size={14} /> Carica</Button></>}>
          {(doc.photos || []).length === 0 ? <p className="text-xs text-slate-400">Nessuna foto.</p> : (
            <div className="grid grid-cols-3 gap-2">
              {doc.photos.map((p) => (
                <div key={p.id} className="group relative overflow-hidden rounded-lg border border-slate-100">
                  <img src={p.url} alt={p.name} className="h-24 w-full object-cover" />
                  {!readonly && <button onClick={() => delItem("photos", p.id)} className="absolute right-1 top-1 rounded bg-black/50 p-1 text-white opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>}
                </div>
              ))}
            </div>
          )}
        </Section>

        <VideoSection doc={doc} readonly={readonly} onAdd={(v) => addItem("videos", v)} onDel={(vid) => delItem("videos", vid)} />
      </div>

      {/* Preventivi + Arredi */}
      <div className="grid gap-4 lg:grid-cols-2">
        <RepeaterSection icon={Wrench} title="Preventivi lavori" listname="work_quotes" testid="pipeline-quotes"
          rows={doc.work_quotes} readonly={readonly} onAdd={addItem} onDel={delItem} onUpd={updItem}
          template={{ supplier: "", description: "", amount: "", status: "richiesto" }}
          cols={[["supplier", "Fornitore"], ["description", "Descrizione"], ["amount", "Importo €"], ["status", "Stato"]]} />
        <RepeaterSection icon={Sofa} title="Arredi da acquistare" listname="furniture" testid="pipeline-furniture"
          rows={doc.furniture} readonly={readonly} onAdd={addItem} onDel={delItem} onUpd={updItem}
          template={{ item: "", qty: "1", estimated_cost: "", notes: "" }}
          cols={[["item", "Articolo"], ["qty", "Q.tà"], ["estimated_cost", "Costo stim. €"], ["notes", "Note"]]} />
      </div>

      {/* Checklist + sopralluoghi + scadenze */}
      <RepeaterSection icon={ListChecks} title="Checklist preparazione" listname="prep_checklist" testid="pipeline-checklist"
        rows={doc.prep_checklist} readonly={readonly} onAdd={addItem} onDel={delItem} onUpd={updItem}
        template={{ label: "", done: false }} checkField="done"
        cols={[["label", "Attività"]]} />
      <div className="grid gap-4 lg:grid-cols-2">
        <RepeaterSection icon={MapPinned} title="Sopralluoghi" listname="site_visits" testid="pipeline-visits"
          rows={doc.site_visits} readonly={readonly} onAdd={addItem} onDel={delItem} onUpd={updItem}
          template={{ date: "", time: "", with_who: "", outcome: "", notes: "" }}
          cols={[["date", "Data"], ["time", "Ora"], ["with_who", "Con chi"], ["outcome", "Esito"]]} />
        <RepeaterSection icon={CalendarClock} title="Scadenze" listname="deadlines" testid="pipeline-deadlines"
          rows={doc.deadlines} readonly={readonly} onAdd={addItem} onDel={delItem} onUpd={updItem}
          template={{ label: "", date: "", done: false }} checkField="done"
          cols={[["label", "Scadenza"], ["date", "Data"]]} />
      </div>

      {!readonly && (
        <div className="flex justify-between">
          <Button variant="ghost" className="gap-2 text-red-500" onClick={async () => { if (window.confirm("Eliminare questa opportunità?")) { try { await api.delete(`/pipeline/${id}`); toast.success("Eliminata"); onBack(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } } }} data-testid="pipeline-delete"><Trash2 size={15} /> Elimina opportunità</Button>
          <Button className="gap-2" onClick={saveMain} disabled={saving}><Save size={15} /> Salva modifiche</Button>
        </div>
      )}

      {/* Convert dialog */}
      <Dialog open={showConvert} onOpenChange={setShowConvert}>
        <DialogContent data-testid="pipeline-convert-dialog">
          <DialogHeader>
            <DialogTitle>Converti in Appartamento</DialogTitle>
            <DialogDescription>Verrà creato un nuovo appartamento (stato "in acquisizione") con i dati della trattativa. Il record resterà come storico commerciale in sola lettura.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-700">Proprietario</label>
            <Select value={conv.owner_action} onValueChange={(v) => setConv({ ...conv, owner_action: v })}>
              <SelectTrigger data-testid="pc-owner-action"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Decido dopo (nessun proprietario ora)</SelectItem>
                <SelectItem value="link_existing">Collega a un proprietario esistente</SelectItem>
                <SelectItem value="create_new">Crea nuovo proprietario dai dati referente</SelectItem>
              </SelectContent>
            </Select>
            {conv.owner_action === "link_existing" && (
              <Select value={conv.owner_id || ""} onValueChange={(v) => setConv({ ...conv, owner_id: v })}>
                <SelectTrigger data-testid="pc-owner-select"><SelectValue placeholder="Scegli proprietario" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {conv.owner_action === "create_new" && (
              <Input placeholder="Nome nuovo proprietario" value={conv.new_owner_name} onChange={(e) => setConv({ ...conv, new_owner_name: e.target.value })} data-testid="pc-new-owner-name" />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConvert(false)}>Annulla</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={doConvert} data-testid="pc-confirm">Converti</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VideoSection({ doc, readonly, onAdd, onDel }) {
  const [url, setUrl] = useState(""); const [title, setTitle] = useState("");
  return (
    <Section icon={Video} title="Video" testid="pipeline-videos"
      action={!readonly && <div className="flex items-center gap-1">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titolo" className="h-8 w-28" />
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Link YouTube/Drive" className="h-8 w-40" data-testid="pd-video-url" />
        <Button size="sm" variant="outline" onClick={() => { if (url) { onAdd({ type: "external", url, title, provider: "external" }); setUrl(""); setTitle(""); } }} data-testid="pd-video-add"><Plus size={14} /></Button>
      </div>}>
      {(doc.videos || []).length === 0 ? <p className="text-xs text-slate-400">Nessun video.</p> : (
        <div className="space-y-2">
          {doc.videos.map((v) => (
            <div key={v.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 p-2 text-sm">
              <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 truncate text-indigo-600"><Video size={14} /> {v.title || v.url}</a>
              {!readonly && <Button size="icon" variant="ghost" className="text-red-500" onClick={() => onDel(v.id)}><Trash2 size={14} /></Button>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function RepeaterSection({ icon, title, listname, testid, rows, readonly, onAdd, onDel, onUpd, template, cols, checkField }) {
  return (
    <Section icon={icon} title={title} testid={testid}
      action={!readonly && <Button size="sm" variant="outline" className="gap-1" onClick={() => onAdd(listname, { ...template })} data-testid={`${testid}-add`}><Plus size={14} /> Aggiungi</Button>}>
      {(rows || []).length === 0 ? <p className="text-xs text-slate-400">Nessuna voce.</p> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-100 p-2">
              {checkField && <Switch disabled={readonly} checked={!!r[checkField]} onCheckedChange={(v) => onUpd && onUpd(listname, r.id, { [checkField]: v })} />}
              {cols.map(([f, ph]) => (
                <Input key={f} disabled={readonly} className="h-8 min-w-[110px] flex-1" defaultValue={r[f] || ""} placeholder={ph}
                  onBlur={(e) => onUpd ? onUpd(listname, r.id, { [f]: e.target.value }) : onUpd} />
              ))}
              {!readonly && <Button size="icon" variant="ghost" className="text-red-500" onClick={() => onDel(listname, r.id)}><Trash2 size={14} /></Button>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}
